
import { useStore } from '@/store'
import { BetType, EventBuss } from '@/types/global'
import { abbreviateNumber } from '@/utils'
import {
    ChipOption,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    StyleValue
} from 'vue'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    emits: ['place-bet', 'on-error'],
    props: {
        label: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        ratio: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        color: {
            type: Number as PropType<number>,
            required: true
        },
        confirmedBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        tempBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        totalBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        betType: {
            type: Number as PropType<number>,
            require: true
        },
        width: {
            type: Number as PropType<number>,
            required: true
        },
        height: {
            type: Number as PropType<number>,
            required: true
        },
        mWidth: {
            type: Number as PropType<number>,
            required: true
        },
        mHeight: {
            type: Number as PropType<number>,
            required: true
        },

        isGrayMode: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        allowBet: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        showFlashingBg: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        errorMessage: {
            type: String as PropType<string>,
            required: false,
            default: ''
        }
    },
    setup(props, { emit }) {
        const emitter = inject('emitter') as Emitter
        const store = useStore()
        const { t } = useI18n()
        const chipsUI = ref<
            {
                x: number
                y: number
                amount: number
                class: string
                type: 'temp' | 'confirmed'
            }[]
        >([])

        onMounted(() => {
            emitter.on(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        const betColor = computed(() => {
            const { betType } = props

            let result = ''

            switch (betType) {
                case BetType.BANKER:
                case BetType.DRAGON:
                case BetType.BANKER_PAIR:
                case BetType.BANKER_SUPER6:
                case BetType.DRAGON_7:
                    result = 'red'
                    break
                case BetType.PLAYER:
                case BetType.TIGER:
                case BetType.PLAYER_PAIR:
                case BetType.BEAR_8:
                    result = 'blue'
                    break
                case BetType.BC_TIE:
                case BetType.DT_TIE:
                    result = 'green'
                    break
                case BetType.SUPER6:
                case BetType.SMALL_TIGER:
                case BetType.BIG_TIGER:
                    result = 'super6'
                    break
            }

            // if (color === 1) return 'red'
            // else if (color === 2) return 'green'
            // else if (color === 3) return 'blue'

            if (props.isGrayMode) return 'gray'
            return result
        })

        const sumOfTempBetandConfirmedBet = computed(() => {
            const confirmedBet = props.confirmedBet ?? 0
            const tempBet = props.tempBet ?? 0
            return confirmedBet + tempBet
        })

        const toShortValue = (value: number) => {
            return abbreviateNumber(value)
        }

        const placeBet = () => {
            if (props.isGrayMode && props.allowBet) {
                let error = props.errorMessage
                if (!error) {
                    if (props.betType === BetType.DRAGON_7) {
                        error = t('prompts.d7error')
                    } else if (props.betType === BetType.BEAR_8) {
                        error = t('prompts.b8error')
                    } else if (props.betType === BetType.BIG_TIGER) {
                        error = t('prompts.bigtigererror')
                    } else if (props.betType === BetType.SMALL_TIGER) {
                        error = t('prompts.smalltigererror')
                    } else if (props.betType === BetType.SUPER6) {
                        error = t('prompts.super6tigermutual')
                    }
                }

                if (error) emit('on-error', error)
                return
            }

            if (!props.allowBet && props.isGrayMode) {
                return
            }

            emit('place-bet', props.betType)
        }

        const removeTempUIBet = () => {
            chipsUI.value = chipsUI.value.filter((i) => i.type !== 'temp')
        }

        const applyTempUIBet = () => {
            chipsUI.value = chipsUI.value.map((i) => {
                if (i.type === 'temp') i.type = 'confirmed'
                return i
            })
        }

        const breakAmountIntoChips = (amount: number): number[] => {
            const values = [...chips.value.value]
                .filter((v) => typeof v === 'number' && v > 0)
                .sort((a, b) => b - a)
            const parts: number[] = []
            let remaining = amount
            for (const v of values) {
                while (remaining >= v) {
                    parts.push(v)
                    remaining -= v
                }
            }
            if (remaining > 0) parts.push(remaining)
            return parts.length > 0 ? parts : [amount]
        }

        const pushChipUI = (amount: number, type: 'temp' | 'confirmed') => {
            let w = 0
            let h = 0
            let chipSize = 0

            if (deviceOrientation.value === 'landscape') {
                w = props.width
                h = props.height
                chipSize = 20
            } else {
                w = props.mWidth
                h = props.mHeight
                chipSize = 30
            }

            const chipHalf = chipSize / 2

            let startY = Math.floor(h / 2)
            let startX = Math.floor(w / 2)

            if (chipsUI.value.length > 0) {
                const lastItem = chipsUI.value[chipsUI.value.length - 1]
                startY = lastItem.y
                startX = lastItem.x
            }

            let _x = getRandomPosition(chipHalf, w - chipHalf)
            let xTries = 0
            while (Math.abs(_x - startX) > chipSize && xTries < 20) {
                _x = getRandomPosition(chipHalf, w)

                if (_x + chipSize > w) {
                    const offset = Math.abs(_x + chipSize - w)
                    _x = _x - offset
                }
                xTries++
            }

            let _y = getRandomPosition(chipHalf, h - chipHalf)
            let yTries = 0
            while (Math.abs(_y - startY) > chipSize && yTries < 20) {
                _y = getRandomPosition(chipHalf, h)

                if (_y + chipSize > h) {
                    const offset = Math.abs(_y + chipSize - h)
                    _y = _y - offset
                }
                yTries++
            }

            let _chipIndex = chips.value.value.indexOf(amount)
            if (_chipIndex === -1) _chipIndex = 22

            chipsUI.value.push({
                x: _x,
                y: _y,
                amount,
                class: `_chip_${_chipIndex}`,
                type
            })
        }

        const handleOnPlaceChip = (arg: {
            t: number
            amt: number
            type: 'temp' | 'confirmed'
        }) => {
            if (arg.t !== props.betType) return

            if (arg.type === 'confirmed') {
                const existingSum = chipsUI.value.reduce(
                    (sum, chip) => sum + chip.amount,
                    0
                )
                // Keep the chips the player placed (e.g. 3x100) instead of
                // collapsing them into one total chip after confirm/sync.
                if (chipsUI.value.length > 0 && existingSum === arg.amt) {
                    chipsUI.value = chipsUI.value.map((chip) => ({
                        ...chip,
                        type: 'confirmed'
                    }))
                    return
                }

                chipsUI.value = []
                breakAmountIntoChips(arg.amt).forEach((amount) => {
                    pushChipUI(amount, 'confirmed')
                })
                return
            }

            pushChipUI(arg.amt, arg.type)
        }

        const getChipPositionStyle = (post: number): string => {
            const _var =
                deviceOrientation.value === 'landscape'
                    ? 'var(--pc1)'
                    : 'var(--mp1)'
            return `calc(${_var}*${post})`
        }

        const resetBetUI = () => {
            chipsUI.value = []
        }

        const getRandomPosition = (min: number, max: number) => {
            return Math.floor(Math.random() * (max - min + 1) + min)
        }
        const chips = computed((): ChipOption => store.getters['chips'])

        const deviceOrientation = computed(
            (): 'landscape' | 'portrait' => store.getters['deviceOrientation']
        )

        const chipContainerStyle = computed((): StyleValue | undefined => {
            if (props.width > 0 && props.height > 0) {
                const w =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.width)
                        : Math.abs(props.mWidth)
                const h =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.height)
                        : Math.abs(props.mHeight)

                const _var =
                    deviceOrientation.value === 'landscape'
                        ? 'var(--pc1)'
                        : 'var(--mp1)'
                return {
                    width: `calc(${_var}*${w})`,
                    height: `calc(${_var}*${h})`
                }
            }
            return undefined
        })

        return {
            betColor,
            sumOfTempBetandConfirmedBet,
            chipsUI,
            placeBet,
            toShortValue,
            handleOnPlaceChip,
            resetBetUI,
            removeTempUIBet,
            applyTempUIBet,
            getChipPositionStyle,
            chipContainerStyle,
            t
        }
    }
})
